// src/components/OfflineBanner.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Slim banner shown below the mobile top header when the device is offline
//  or has pending unsynced writes.
//
//  States:
//    offline  — red   — "You're offline — changes will sync when reconnected"
//    pending  — amber — "Syncing X change(s)…"
//    synced   — hidden (renders null)
//
//  The banner animates in/out with a CSS transition so it doesn't cause a
//  layout jump in the sticky header. Height is fixed at 28px so the main
//  content's paddingTop only needs to account for the banner when it is shown.
//
//  PLACEMENT in Layout.jsx (mobile only):
//    Inside the fixed top-header div, directly below the avatar/title row.
//    The banner is excluded from the tablet layout (SideNav has its own space).
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { useSyncStatus } from '@/contexts/SyncStatusContext';
import { WifiOff, RefreshCw } from 'lucide-react';

export default function OfflineBanner() {
  const { status, pendingCount } = useSyncStatus();

  // Synced and online — render nothing, zero DOM cost.
  if (status === 'synced') return null;

  const isOffline = status === 'offline';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            '6px',
        height:         '26px',
        fontSize:       '0.72rem',
        fontWeight:     600,
        letterSpacing:  '0.01em',
        paddingLeft:    '12px',
        paddingRight:   '12px',
        // Colours match Sheepheard's Tailwind palette
        background:  isOffline ? 'rgba(239,68,68,0.12)'  : 'rgba(245,158,11,0.12)',
        color:       isOffline ? '#ef4444'               : '#d97706',
        borderBottom: isOffline
          ? '1px solid rgba(239,68,68,0.25)'
          : '1px solid rgba(245,158,11,0.25)',
        // Smooth slide-in from top when first appearing
        transition: 'opacity 0.2s ease',
      }}
    >
      {isOffline ? (
        <>
          <WifiOff size={11} strokeWidth={2.5} aria-hidden />
          You&rsquo;re offline — changes will sync when reconnected
        </>
      ) : (
        <>
          <RefreshCw size={11} strokeWidth={2.5} aria-hidden
            style={{ animation: 'spin 1.4s linear infinite' }} />
          Syncing {pendingCount} change{pendingCount !== 1 ? 's' : ''}…
        </>
      )}
    </div>
  );
}